import React from 'react'
import { Badge } from '@chakra-ui/react'
import { ColDef, ICellRendererParams } from 'ag-grid-community'
import { renewals } from '@homevest/utils'

import { formatUTCDate } from 'lib/date-time'
import { formatRenewalStatus } from 'lib/renewals'
import { PendingRenewalDataRow } from '../types'

const { RENEWAL_STATUSES } = renewals

const ADDRESS: ColDef<PendingRenewalDataRow> = {
  headerName: 'Address',
  field: 'home_details.address',
  valueFormatter: (params) => params.value + ' | ' + params.data?.market_name
}

const TENANT: ColDef<PendingRenewalDataRow> = {
  headerName: 'Tenant',
  field: 'primary_tenant_name'
}

const LEASE_END_DATE: ColDef<PendingRenewalDataRow> = {
  headerName: 'Lease End Date',
  field: 'lease_ends_at',
  valueFormatter: (params) => {
    try {
      return formatUTCDate(new Date(params.value), 'MMM d, Y')
    } catch {
      return params.value
    }
  }
}

const STATUS: ColDef<PendingRenewalDataRow> = {
  headerName: 'Status',
  field: 'status',
  valueFormatter: (params) => formatRenewalStatus(params.value),
  cellRenderer: (params: ICellRendererParams<PendingRenewalDataRow, string>) => (
    <div className='flex items-center gap-2'>
      {params.data?.lease_variant === 'kcpoa' && <Badge colorScheme='teal'>RPP</Badge>}
      <span>{params.valueFormatted || formatRenewalStatus(params.value)}</span>
    </div>
  )
}

const FUND: ColDef<PendingRenewalDataRow> = {
  headerName: 'Fund',
  field: 'fund'
}

const ADMIN_SIGN_REQUIRED: ColDef<PendingRenewalDataRow> = {
  headerName: 'Admin Sign Required',
  field: 'action_required',
  valueGetter: (params) =>
    params.data?.status === RENEWAL_STATUSES.PENDING_UPANDUP_SIGNATURE &&
    params.data?.signers?.some(
      (signer) => signer.role === 'admin' && signer.status === 'pending_signature'
    ),
  valueFormatter: (params) => (params.value ? '✅' : ' ❌')
}

const MOST_RECENT_NOTE: ColDef<PendingRenewalDataRow> = {
  headerName: 'Most Recent Note',
  field: 'most_recent_note',
  cellRenderer: (params: ICellRendererParams<PendingRenewalDataRow, string | undefined>) => {
    const { data, value } = params
    if (value && data?.most_recent_note_created_at) {
      const createdDate = formatUTCDate(data.most_recent_note_created_at, 'yyyy-MM-dd')
      return (
        <div>
          {'['}
          <span className='font-semibold'>{createdDate}</span>
          {'] '}
          {value}
        </div>
      )
    }
    return ''
  },
  autoHeight: true
}

export const PENDING_COLUMNS = [
  ADDRESS,
  FUND,
  TENANT,
  LEASE_END_DATE,
  STATUS,
  MOST_RECENT_NOTE,
  ADMIN_SIGN_REQUIRED
]
